import { Link } from "react-router-dom";
import type { TemporalFeature } from "../../../api/userManagement.service";
import { ROUTES } from "../../../routes";
import styles from "../UserManagement.module.css";

type FulfilmentProductsSectionProps = {
  temporalFeatures: TemporalFeature[];
  companyId: string;
};

function stateBadge(feature: TemporalFeature) {
  if (feature.state === "future") {
    return (
      <span className={`${styles.statusBadge} ${styles.statusAuto}`}>
        Future{feature.from ? ` from ${feature.from}` : ""}
      </span>
    );
  }
  return (
    <span className={`${styles.statusBadge} ${styles.statusActive ?? ""}`}>
      Current{feature.until ? ` until ${feature.until}` : ""}
    </span>
  );
}

/**
 * Read-only firm products view shown when firm-features-source=fulfilment: entitlements derive
 * entirely from the firm's subscriptions and packages, so there is no manual add/remove here —
 * staff manage them through Subscriptions and Packages.
 */
export function FulfilmentProductsSection({
  temporalFeatures,
  companyId,
}: FulfilmentProductsSectionProps) {
  const sorted = [...temporalFeatures].sort((first, second) =>
    first.feature.localeCompare(second.feature),
  );

  return (
    <div className={styles.section}>
      <h2
        className={styles.sectionTitle}
        style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}
      >
        Firm Products
        <span
          style={{
            fontSize: "var(--font-size-xs, 0.75rem)",
            fontWeight: "var(--font-weight-medium, 500)",
            color: "var(--color-success, #15803d)",
            border: "1px solid var(--color-success, #15803d)",
            borderRadius: "999px",
            padding: "0.125rem 0.625rem",
          }}
        >
          From Fulfilment
        </span>
      </h2>

      <p
        style={{
          fontSize: "var(--font-size-sm)",
          color: "var(--color-text-secondary)",
          marginBottom: "0.75rem",
        }}
      >
        Products derive from the firm's subscriptions and packages — manage them via{" "}
        <Link
          to={
            companyId ? ROUTES.fulfilment.firmDashboard(companyId) : ROUTES.fulfilment.subscriptions
          }
        >
          Subscriptions
        </Link>{" "}
        and <Link to="/fulfilment/packages">Packages</Link>.
      </p>

      {sorted.length === 0 ? (
        <p style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
          No features resolved for this firm — it has no active subscriptions and no default package
          applies.
        </p>
      ) : (
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--font-size-sm)" }}
        >
          <thead>
            <tr>
              {["Feature", "State"].map(header => (
                <th
                  key={header}
                  style={{
                    textAlign: "left",
                    padding: "0.5rem 0.75rem",
                    fontWeight: "var(--font-weight-semibold)",
                    color: "var(--color-text-secondary)",
                    borderBottom: "1px solid var(--color-border)",
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(feature => (
              <tr key={`${feature.feature}-${feature.state}`}>
                <td
                  style={{
                    padding: "0.5rem 0.75rem",
                    borderBottom: "1px solid var(--color-border)",
                  }}
                >
                  {feature.feature}
                </td>
                <td
                  style={{
                    padding: "0.5rem 0.75rem",
                    borderBottom: "1px solid var(--color-border)",
                  }}
                >
                  {stateBadge(feature)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default FulfilmentProductsSection;
